import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink, FileText } from 'lucide-react';

interface PdfViewerProps {
  fileUrl: string;
  fileName?: string;
  fileSizeKB?: number;
  onClose: () => void;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, fileName, fileSizeKB, onClose }) => {

  const handleDownload = async () => {
    if (!fileUrl) {
      return;
    }

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || fileUrl.split('/').pop() || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);

  const sizeLabel =
    typeof fileSizeKB === 'number' && !Number.isNaN(fileSizeKB)
      ? `${fileSizeKB.toFixed(1)} KB`
      : undefined;

  const displayName = (fileName || fileUrl.split('/').pop() || 'Document').replace(/\.pdf$/i, '');

  return createPortal(
    <div
      className="bg-page-bg flex flex-col"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        zIndex: 2147483647,
      }}
    >
      <div className="bg-blueprint-blue h-[60px] px-4 flex items-center justify-between flex-shrink-0">
        {/* Left: file info */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[13px] font-medium text-white truncate max-w-[240px] sm:max-w-[480px]">
              {displayName}
            </span>
            {sizeLabel && (
              <span className="text-[11px] text-white/50 mt-0.5">PDF · {sizeLabel}</span>
            )}
          </div>
        </div>

        {/* Right: open / download / close */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenInNewTab}
            className="flex items-center gap-1.5 px-3 h-8 text-[12px] font-medium text-white border border-white/20 rounded-md hover:bg-white/10 transition-colors"
            title="Open in New Tab"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Open</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 h-8 text-[12px] font-medium bg-hero-red text-white rounded-md hover:bg-hero-red-60 transition-colors"
            title="Download PDF"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </button>
          <div className="w-px h-5 bg-white/15 mx-1" />
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-colors"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-page-bg min-h-0">
        <iframe
          src={`${fileUrl}#toolbar=0&navpanes=0&statusbar=0&view=FitH`}
          className="border-0 w-full h-full block"
          style={{ width: '100%', height: '100%', display: 'block', margin: 0, padding: 0, border: 'none' }}
          title={fileName || 'PDF Viewer'}
          allow="fullscreen"
        />
      </div>
    </div>,
    document.documentElement
  );
};

export default PdfViewer;

